/**
 * @architecture-header 1
 * @layer module
 * @domain product-updates
 * @responsibility Hold the versioned, static "what's new" content Vispo ships in-bundle, behind a
 *   guarded runtime-validating load boundary (round-1 finding 2).
 * @state-owner immutable module constant
 * @allowed-imports none
 * @forbidden network; database access; runtime state
 * @public-api ProductUpdate, PRODUCT_UPDATES, parseProductUpdates
 * @side-effects none
 * @tests content.test.ts
 */

export interface ProductUpdate {
  id: string;
  publishedAt: string;
  title: { 'zh-TW': string; en: string };
  body: { 'zh-TW': string; en: string };
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isValidLocaleMap(value: unknown): value is { 'zh-TW': string; en: string } {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return isNonEmptyString(candidate['zh-TW']) && isNonEmptyString(candidate.en);
}

function isValidProductUpdate(value: unknown): value is ProductUpdate {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const candidate = value as Record<string, unknown>;
  return (
    isNonEmptyString(candidate.id) &&
    isNonEmptyString(candidate.publishedAt) &&
    isValidLocaleMap(candidate.title) &&
    isValidLocaleMap(candidate.body)
  );
}

// Round-1 finding 2: a static compile-time import cannot itself "fail to load" at runtime the way
// a dynamic resource fetch can -- this is the honest, real content-load guard a zero-backend,
// in-bundle static content design can actually provide. Every entry is validated at this module
// boundary and a malformed one is silently dropped, never crashed on and never rendered as
// garbage; both consumers (ShellPopovers.tsx, MobileInboxPage.tsx) read PRODUCT_UPDATES below,
// which is this function's own output, never the raw literal array directly.
export function parseProductUpdates(raw: readonly unknown[]): ProductUpdate[] {
  return raw.filter(isValidProductUpdate);
}

// Newest-first (CONTRACT APPROVED R3) -- matches every other list in this codebase. Append-only in
// spirit: never reuse an id for a different update, never delete a shipped entry -- the seen-cursor
// (modules/product-updates/index.ts) compares against these ids directly.
const PRODUCT_UPDATES_SOURCE: readonly ProductUpdate[] = [
  {
    id: 'sh-f09-what-is-new',
    publishedAt: '2026-08-24',
    title: {
      'zh-TW': '「有什麼新的」上線了',
      en: 'What’s New has launched',
    },
    body: {
      'zh-TW': '收件匣旁新增了「有什麼新的」，隨時查看 Vispo 最近推出的新功能與改進。',
      en: 'The inbox now has a What’s New tab — check it any time to see what Vispo has recently shipped.',
    },
  },
];

export const PRODUCT_UPDATES: readonly ProductUpdate[] =
  parseProductUpdates(PRODUCT_UPDATES_SOURCE);
